import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { AppDispatch } from '../../store';
import { PostDraft, draftUtils } from '../../Utils/draftStorage';
import { PlaceDraft, placeDraftUtils } from '../../Utils/placeDraftStorage';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';

const Drafts: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const postDrafts = useSelector((state: RootState) => state.draft.drafts);
  const placeDrafts = useSelector((state: RootState) => state.placeDraft?.drafts || []);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<string | null>(null);
  const [draftType, setDraftType] = useState<'post' | 'place'>('post');

  // Get current path to determine draft type
  const currentPath = window.location.pathname;
  
  // Set draft type based on current page - more explicit detection
  React.useEffect(() => {
    if (currentPath.includes('/place/drafts')) {
      setDraftType('place');
    } else if (currentPath.includes('/post/drafts')) {
      setDraftType('post');
    } else {
      // Default to post for backward compatibility
      setDraftType('post');
    }
  }, [currentPath]);

  // Get appropriate drafts based on type
  const drafts = draftType === 'post' ? postDrafts : placeDrafts;
  

  useEffect(() => {
    // Drafts are now managed by Redux and persisted automatically
    setLoading(false);
  }, []);

  const handleEditDraft = (draft: PostDraft | PlaceDraft) => {
    if (draftType === 'post') {
      navigate('/post/input', { state: { draft, isEdit: true } });
    } else {
      navigate(`/createplace?draftId=${draft.id}`);
    }
  };

  const handleViewDraft = (draft: PostDraft | PlaceDraft) => {
    if (draftType === 'post') {
      navigate('/post/input', { state: { draft, isPreview: true } });
    } else {
      navigate(`/createplace?draftId=${draft.id}&preview=true`);
    }
  };

  const handleDeleteClick = (draftId: string) => {
    setDraftToDelete(draftId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (draftToDelete) {
      try {
        if (draftType === 'post') {
          draftUtils.deleteDraft(dispatch, draftToDelete);
        } else {
          placeDraftUtils.deleteDraft(dispatch, draftToDelete);
        }
        setDeleteDialogOpen(false);
        setDraftToDelete(null);
      } catch (error) {
        console.error('Error deleting draft:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDraftToDelete(null);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading drafts...</Typography>
      </Box>
    );
  }

  if (drafts.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          No drafts found. Start creating a post and save it as draft!
        </Alert>
      
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <TopNavBar title={`${draftType === 'post' ? 'Post' : 'Place'} Drafts`} canGoBack/>
      <Stack spacing={2}>
        {drafts.map((draft) => (
          <Card key={draft.id} sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>

                {/* Draft Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {draftType === 'post' 
                        ? (draft as PostDraft).title || 'Untitled Post Draft'
                        : (draft as PlaceDraft).name || 'Untitled Place Draft'
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {draftType === 'post' 
                        ? (draft as PostDraft).description || 'No description'
                        : (draft as PlaceDraft).description || 'No description'
                      }
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(draft.createdAt).toLocaleDateString()}
                      {draft.updatedAt !== draft.createdAt && (
                        <> • Updated: {new Date(draft.updatedAt).toLocaleDateString()}</>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                  
                      <IconButton
                        size="small"
                        onClick={() => handleEditDraft(draft)}
                        sx={{ color: '#F57C00' }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(draft.id)}
                        sx={{ color: '#f44336' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Draft</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this draft? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Drafts;
